import { useState, useEffect, useRef, useCallback } from "react";
import api from "../utils/api";
import STYLES from "./Dashboards/AdminChatStyles.jsx";
import INTERVIEW_TEMPLATES from "./Dashboards/assets/Interview_Templates";
import {
  IconPlus,
  IconSearch,
  IconChat,
  IconMenu,
  IconSend,
  IconSpin,
  IconInfo,
} from "../utils/Icons.js";
import {
  initials,
  fmtTime,
  fmtDate,
  relTime,
} from "../utils/chatFormatters.js";
import UserPicker from "./Dashboards/assets/UserPickerModal.jsx";
import ApplicantPanel from "./Dashboards/assets/ChatApplicantPanel.jsx";

let injected = false;
function inject() {
  if (injected || document.getElementById("ql-achat-v2")) return;
  const s = document.createElement("style");
  s.id = "ql-achat-v2";
  s.textContent = STYLES;
  document.head.appendChild(s);
  injected = true;
}

/* ══ AdminChat ════════════════════════════════════════════════════════════════ */
export default function AdminChat({ admin }) {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const textareaRef = useRef(null);
  // ── FIX: track previous message count to distinguish initial load vs new message ──
  const prevMsgCount = useRef(0);

  useEffect(() => {
    inject();
  }, []);

  /* ── Data fetching ── */
  const fetchConvs = useCallback(async (silent = false) => {
    if (!silent) setLoadingConvs(true);
    try {
      const { data } = await api.get("/chat/conversations");
      setConversations(data);
    } catch {
    } finally {
      if (!silent) setLoadingConvs(false);
    }
  }, []);

  const fetchMsgs = useCallback(
    async (userId, silent = false) => {
      if (!userId) return;
      if (!silent) setLoadingMsgs(true);
      try {
        const { data } = await api.get(`/chat/${userId}`);
        setMessages(data);
        if (!silent) fetchConvs(true);
      } catch {
      } finally {
        if (!silent) setLoadingMsgs(false);
      }
    },
    [fetchConvs],
  );

  useEffect(() => {
    fetchConvs();
  }, [fetchConvs]);

  // Poll every 4s
  useEffect(() => {
    pollRef.current = setInterval(() => {
      fetchConvs(true);
      if (activeConv) fetchMsgs(activeConv.userId, true);
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [activeConv, fetchConvs, fetchMsgs]);

  useEffect(() => {
    if (activeConv) fetchMsgs(activeConv.userId);
  }, [activeConv?.userId]); // eslint-disable-line

  // ── FIX: WhatsApp/ChatGPT scroll behavior ──────────────────────────────────
  // On initial load → instant jump (no visible animation)
  // On new message → smooth scroll
  useEffect(() => {
    if (!bottomRef.current) return;

    const isInitialLoad = prevMsgCount.current === 0 && messages.length > 0;
    prevMsgCount.current = messages.length;

    if (isInitialLoad) {
      // Jump instantly — user opens chat and is already at the bottom
      bottomRef.current.scrollIntoView({ behavior: "instant" });
    } else if (messages.length > 0) {
      // A new message arrived — smooth scroll down
      setTimeout(
        () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
        60,
      );
    }
  }, [messages]);

  // Reset prevMsgCount when switching conversations so next load is treated as initial
  useEffect(() => {
    prevMsgCount.current = 0;
  }, [activeConv?.userId]);

  /* ── Send ── */
  const send = async () => {
    const t = text.trim();
    if (!t || sending || !activeConv) return;
    setSending(true);
    setText("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    try {
      const { data } = await api.post("/chat/send", {
        text: t,
        targetUserId: activeConv.userId,
      });
      setMessages((prev) => [...prev, data]);
      fetchConvs(true);
    } catch {
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const openConv = (userId, userName, userEmail) => {
    setActiveConv({ userId, userName, userEmail });
    setMessages([]);
    setSidebarOpen(false);
  };

  const handlePickUser = (u) => {
    setShowPicker(false);
    openConv(u._id, u.name || u.email, u.email);
  };

  /* ── Derived ── */
  const filteredConvs = conversations.filter(
    (c) =>
      c.userName.toLowerCase().includes(searchQ.toLowerCase()) ||
      c.userEmail.toLowerCase().includes(searchQ.toLowerCase()),
  );
  const totalUnread = conversations.reduce((s, c) => s + (c.unread || 0), 0);
  const grouped = messages.reduce((acc, msg) => {
    const d = fmtDate(msg.createdAt);
    if (!acc.length || acc[acc.length - 1].date !== d)
      acc.push({ date: d, msgs: [] });
    acc[acc.length - 1].msgs.push(msg);
    return acc;
  }, []);

  /* ── Render ── */
  return (
    <>
      {showPicker && (
        <UserPicker
          onSelect={handlePickUser}
          onClose={() => setShowPicker(false)}
        />
      )}

      <div className="achat-root">
        {/* Mobile backdrop */}
        {sidebarOpen && (
          <div
            className="achat-sidebar-backdrop"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* ── Sidebar ── */}
        <div className={`achat-sidebar${sidebarOpen ? " open" : ""}`}>
          <div className="achat-sb-header">
            <div className="achat-sb-title-row">
              <div className="achat-sb-title">
                <IconChat />
                <span>Messages</span>
                {totalUnread > 0 && (
                  <span className="achat-total-badge">{totalUnread}</span>
                )}
              </div>
              <button
                className="achat-new-btn"
                onClick={() => setShowPicker(true)}
              >
                <IconPlus /> New
              </button>
            </div>
            <div className="achat-search-wrap">
              <IconSearch cls="achat-search-icon" />
              <input
                className="achat-search"
                placeholder="Search conversations…"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
              />
            </div>
          </div>

          <div className="achat-conv-list">
            {loadingConvs ? (
              <div className="achat-empty-sb">Loading…</div>
            ) : filteredConvs.length === 0 ? (
              <div className="achat-empty-sb">
                {searchQ
                  ? "No conversations match your search."
                  : "No messages yet. Click New to start a conversation."}
              </div>
            ) : (
              filteredConvs.map((c) => (
                <div
                  key={c.conversationId}
                  className={`achat-conv-item${activeConv?.userId === c.userId ? " active" : ""}`}
                  onClick={() => openConv(c.userId, c.userName, c.userEmail)}
                >
                  <div className="achat-conv-av">
                    {initials(c.userName)}
                    {c.unread > 0 && <div className="achat-conv-av-badge" />}
                  </div>
                  <div className="achat-conv-info">
                    <div className="achat-conv-name">{c.userName}</div>
                    <div className="achat-conv-prev">
                      {c.lastSenderRole === "admin" ? "You: " : ""}
                      {c.lastMessage}
                    </div>
                  </div>
                  <div className="achat-conv-meta">
                    <div className="achat-conv-time">{relTime(c.lastAt)}</div>
                    {c.unread > 0 && (
                      <div className="achat-conv-badge">{c.unread}</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Main pane ── */}
        <div className="achat-main">
          {!activeConv ? (
            <div className="achat-no-conv">
              <button
                className="achat-mob-toggle"
                onClick={() => setSidebarOpen(true)}
                style={{ marginBottom: 8 }}
              >
                <IconMenu />
              </button>
              <div className="achat-no-conv-icon">
                <IconChat />
              </div>
              <h3>Select a conversation</h3>
              <p>
                Choose a conversation from the sidebar, or click{" "}
                <strong>New</strong> to message any user.
              </p>
              <button
                className="achat-new-btn"
                style={{ marginTop: 8 }}
                onClick={() => setShowPicker(true)}
              >
                <IconPlus /> Start New Conversation
              </button>
            </div>
          ) : (
            <>
              {/* ── Conversation header ── */}
              <div className="achat-conv-hdr">
                <button
                  className="achat-mob-toggle"
                  onClick={() => setSidebarOpen(true)}
                >
                  <IconMenu />
                </button>

                <div className="achat-conv-hdr-av">
                  {initials(activeConv.userName)}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="achat-conv-hdr-name">
                    {activeConv.userName}
                  </div>
                  <div className="achat-conv-hdr-email">
                    {activeConv.userEmail}
                  </div>
                </div>

                <button
                  className={`achat-panel-btn${panelOpen ? " active" : ""}`}
                  onClick={() => setPanelOpen((o) => !o)}
                  title="View applicant info & change status"
                >
                  <IconInfo />
                  <span
                    style={{ display: "none" }}
                    className="achat-panel-btn-label"
                  >
                    Applicant
                  </span>
                  <span style={{ fontSize: ".7rem" }}>
                    {panelOpen ? "▲" : "▼"}
                  </span>
                </button>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: ".7rem",
                    color: "var(--green)",
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  <svg width="7" height="7" viewBox="0 0 8 8" fill="#22c55e">
                    <circle cx="4" cy="4" r="4" />
                  </svg>
                  Active
                </div>
              </div>

              {/* ── Applicant panel (expandable) ── */}
              <ApplicantPanel conv={activeConv} open={panelOpen} />

              {/* ── Messages ── */}
              <div className="achat-msgs">
                {loadingMsgs && messages.length === 0 ? (
                  <div className="achat-empty-msgs">
                    <IconSpin size={20} />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="achat-empty-msgs">
                    <IconChat />
                    <p>No messages yet. Send the first one!</p>
                  </div>
                ) : (
                  grouped.map((g) => (
                    <div
                      key={g.date}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      <div className="achat-date-sep">{g.date}</div>
                      {g.msgs.map((msg) => {
                        const isMe = msg.senderRole === "admin";
                        return (
                          <div
                            key={msg._id}
                            className={`achat-msg ${isMe ? "me" : "them"}`}
                          >
                            {!isMe && (
                              <div className="achat-msg-label">
                                {activeConv.userName}
                              </div>
                            )}
                            <div className="achat-bubble">{msg.text}</div>
                            <div className="achat-msg-time">
                              {fmtTime(msg.createdAt)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
                <div ref={bottomRef} />
              </div>

              {/* ── Quick-reply templates ── */}
              <div className="achat-templates-bar">
                <div className="achat-templates-label">Quick replies</div>
                <div className="achat-templates-list">
                  {INTERVIEW_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.label}
                      className="achat-tpl-btn"
                      title={tpl.text}
                      onClick={() => {
                        setText(tpl.text);
                        if (textareaRef.current) {
                          textareaRef.current.focus();
                          textareaRef.current.style.height = "auto";
                          textareaRef.current.style.height =
                            Math.min(textareaRef.current.scrollHeight, 120) +
                            "px";
                        }
                      }}
                    >
                      {tpl.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Input ── */}
              <div className="achat-input-row">
                <textarea
                  ref={textareaRef}
                  className="achat-textarea"
                  placeholder={`Reply to ${activeConv.userName}…`}
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height =
                      Math.min(e.target.scrollHeight, 120) + "px";
                  }}
                  onKeyDown={handleKey}
                  rows={1}
                  disabled={sending}
                  autoFocus
                />
                <button
                  className="achat-send-btn"
                  onClick={send}
                  disabled={!text.trim() || sending}
                >
                  {sending ? <IconSpin size={14} /> : <IconSend />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
